import { useUnmount } from 'react-use';
import React, { useCallback } from 'react';
import { Link as ReactLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { pick } from 'lodash';
import { setPrefetchProgress } from '@ts/core/actions/app';
import { isLocal } from './utils';
import presetState from './presetState';
import parseLoadable from './parseLoadable';
import PrefetchObject from './PrefetchObject';

let timeout = 0;

function Link({
    children,
    to = '/',
    prefetchOnMouseEnter: _prefetchOnMouseEnter = false,
    onClick,
    onMouseEnter,
    ...rest
}) {
    const dispatch = useDispatch();
    const loadable = React.useMemo(
        () => (typeof window !== 'undefined' ? parseLoadable(to) : false),
        [to]
    );

    const prefetchOnMouseEnter =
        typeof _prefetchOnMouseEnter === 'boolean' ? _prefetchOnMouseEnter : false;

    const prefetchObject = React.useRef(loadable ? PrefetchObject.create(to, loadable) : null);

    const setProgressHandlers = useCallback(() => {
        dispatch(
            setPrefetchProgress({
                loading: true,
                ...prefetchObject.current.progress,
            })
        );
        prefetchObject.current.onProgress((self) =>
            dispatch(setPrefetchProgress({ loading: true, ...self.progress }))
        );
    }, [dispatch]);

    const cleanProgress = useCallback(() => {
        dispatch(
            setPrefetchProgress({
                loading: false,
                ...prefetchObject.current.progress,
            })
        );

        if (timeout) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(() => {
            dispatch(
                setPrefetchProgress({
                    loading: false,
                    total: 0,
                    loaded: 0,
                })
            );
        }, 300);
    }, [dispatch]);

    const redirect = useCallback(() => {
        // preset state
        try {
            if (prefetchObject.current.isLoaded && prefetchObject.current.result) {
                presetState(dispatch)(prefetchObject.current);
            }
        } catch {}

        // change location
        window.browserHistory.push(to);
    }, [dispatch, to]);

    const prefetch = useCallback(
        (redirectAfter) => {
            if (prefetchObject.current) {
                const f = () => {
                    if (redirectAfter) {
                        redirect();
                        cleanProgress();
                    }
                };

                if (prefetchObject.current.isLoaded) {
                    // state is loaded
                    f();
                } else {
                    if (redirectAfter) {
                        setProgressHandlers();
                    }

                    // handle success loading
                    prefetchObject.current.onLoad(() => f());

                    if (!prefetchObject.current.isLoading) {
                        // handle failed fetch
                        prefetchObject.current.fetch().catch(() => f());
                    }
                }
            }
        },
        [cleanProgress, redirect, setProgressHandlers]
    );

    const click = useCallback(
        (evt) => {
            if (typeof onClick === 'function') {
                onClick(evt);
            }

            const { nodeName, target } = evt.currentTarget;

            if (
                nodeName === 'A' &&
                ((target && target !== '_self') ||
                    evt.metaKey ||
                    evt.ctrlKey ||
                    evt.shiftKey ||
                    (evt.nativeEvent && evt.nativeEvent.which === 2))
            ) {
                // ignore click for new tab / new window behavior
                return;
            }

            if (!isLocal(to)) {
                // ignore click if it's outside our scope (e.g. https://google.com)
                return;
            }

            if (prefetchObject.current) {
                evt.preventDefault();
                PrefetchObject.cancelAllWithout(prefetchObject.current.key);
                prefetch(true);
            }
        },
        [onClick, prefetch, to]
    );

    const mouseEnter = useCallback(
        (evt) => {
            if (typeof onMouseEnter === 'function') {
                onMouseEnter(evt);
            }
            prefetch();
        },
        [prefetch, onMouseEnter]
    );

    useUnmount(() => {
        PrefetchObject.remove(to);
    });

    const linkProps = React.useMemo(
        () => ({
            to,
            onClick: click,
            onMouseEnter: prefetchOnMouseEnter ? mouseEnter : onMouseEnter,
            ...pick(rest, ['className', 'data-test-id', 'rel']),
        }),
        [click, mouseEnter, onMouseEnter, prefetchOnMouseEnter, rest, to]
    );

    return (
        <ReactLink data-test-id="link" {...linkProps}>
            {children}
        </ReactLink>
    );
}

export default Link;
